// Standard library imports
import React, { useEffect, useState, useRef } from 'react';

// External library imports
import { Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import { get as lodashGet, set as lodashSet, cloneDeep } from 'lodash';
import moment from 'moment';

// Internal module imports
import { useLoader, useUserRole } from '../../../../hooks';
import Dropdown from '../../../../components/Inputs/Dropdown';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import Typography from '../../../../components/Typography/Typography';
import { Input, InputWithOutState, TextArea, ToggleButton } from '../../../../components/Inputs/Input';
import { RootBiddingService } from '../../../../services/BiddingService/Root/BiddingService';
import { momentTimeFormater } from '../../../../utils/timeHelper';
import bidClasses from '../Bidding.module.css';
import allDeviceClasses from '../../../../styles/AllDevices.module.css';
import { getTotalHoursForDay } from '../../../../utils/dateHelper';
import { CreateBidValidator } from '../../../../validations/BiddingService/Bidding';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';

const BID_TYPES = [
    {
        label: 'Primary',
        value: 'primary',
    },
    {
        label: 'Secondary',
        value: 'secondary',
    },
];

const getInitialValues = (date, market, capacity = [], price = [], regulationPrice = [], hidePrice = false) => {
    const totalHours = getTotalHoursForDay(momentTimeFormater(date).format('YYYY-MM-DD'));
    let temp = {
        capacity: [],
        price: [],
        ...(market.includes('aFRR') || market.includes('mFRR') ? { regulationPrice: [] } : {}),
    };
    if (capacity.length === totalHours) {
        temp = {
            capacity: capacity,
            price: price,
            ...(market.includes('aFRR') || market.includes('mFRR') ? { regulationPrice: regulationPrice } : {}),
        };
    } else {
        Array.from({ length: totalHours }, (__, idx) => {
            temp.capacity.push(lodashGet(capacity, [idx]));
            temp.price.push(lodashGet(price, [idx]));
            if (market.includes('aFRR') || market.includes('mFRR')) temp.regulationPrice.push(lodashGet(regulationPrice, [idx]));
        });
    }

    if (hidePrice) temp.price = [];

    return temp;
};

const CreateBid = ({ data, closeModal, getBids, editBid = false, existingBid = null, editFunc = null }) => {
    const formikRef = useRef();
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBiddingService : AdminBiddingService;
    const { customerId, market, instance, bidDate, cost = 0 } = data;
    const isFRR = market.includes('aFRR') || market.includes('mFRR');
    const hidePrice = isFRR && !!instance?.energyMarketOnly;
    const [startLoader, stopLoader] = useLoader();
    const defaultDate = bidDate ? moment(bidDate).toDate() : moment().toDate();
    const [date, setDate] = useState(defaultDate);
    const [selectedBidType, setSelectedBidType] = useState(BID_TYPES[0]);
    const [alreadyBidCreatedHours, setAlreadyBidCreatedHours] = useState([]);

    const [bidRequest, setBidRequest] = useState({
        note: null,
        capacity: [],
        price: [],
        regulationPrice: [],
    });

    const [approvedCapacity, setApprovedCapacity] = useState(0);

    const [initialValues, setInitialValues] = useState({
        capacity: [],
        price: [],
        ...(isFRR ? { regulationPrice: [] } : {}),
        bidType: null,
        isExternal: false,
    });

    const [lastBidData, setLastBidData] = useState({
        capacity: [],
        price: [],
        regulationPrice: [],
    });

    const [datahubCostEnergy, setDatahubCostEnergy] = useState([]);

    useEffect(() => {
        setInitialValues(() => ({
            ...getInitialValues(
                date,
                market,
                editBid ? lodashGet(existingBid, ['primary', 'capacity'], []) : [],
                editBid ? lodashGet(existingBid, ['primary', 'price'], []) : [],
                editBid ? lodashGet(existingBid, ['primary', 'regulationPrice'], []) : [],
                hidePrice
            ),
            ...(!isFRR ? { reserveBidIdentification: editBid ? existingBid.reserveBidIdentification : null } : {}),
            bidType: null,
            isExternal: false,
        }));
    }, []);

    useEffect(() => {
        if (customerId && selectedBidType.value) getLastBid();
    }, [selectedBidType.value]);

    useEffect(() => {
        if (instance.value && !editBid) getOtherMarketsBids();
    }, [date, selectedBidType.value]);

    useEffect(() => {
        if (instance.value) getBidRequest();
        if (isFRR) getDatahubCostEnergy();
    }, [date]);

    useEffect(() => {
        const temp = getInitialValues(
            date,
            market,
            lodashGet(lastBidData, ['capacity'], []),
            lodashGet(lastBidData, ['price'], []),
            lodashGet(lastBidData, ['regulationPrice'], []),
            hidePrice
        );

        const tempCopy = cloneDeep(temp);

        if (alreadyBidCreatedHours.length) {
            alreadyBidCreatedHours.map((h) => {
                lodashSet(tempCopy, ['capacity', h], 0);
                lodashSet(tempCopy, ['price', h], 0);
                isFRR && lodashSet(tempCopy, ['regulationPrice', h], 0);
            });
        }
        setInitialValues((prev) => ({ ...prev, ...tempCopy }));
    }, [alreadyBidCreatedHours, lastBidData]);

    const getLastBid = () => {
        service.GetLastBid({ market, customerId, instanceId: instance.value, bidType: selectedBidType.value }, startLoader, handleGetLastBidSuccess, handleError, stopLoader);
    };

    const handleGetLastBidSuccess = ({ data }) => {
        const bid = lodashGet(data, ['data', 'bid']);
        setApprovedCapacity(lodashGet(data, ['data', 'approvedCapacity'], 0));
        if (bid && !lodashGet(existingBid, ['primary', 'capacity'], []).length) {
            const tempDate = moment(bid.date).add(1, 'day').toDate();
            if (!bidDate) setDate(tempDate);
            const capacity = lodashGet(bid, [selectedBidType.value, 'capacity'], []);
            const price = lodashGet(bid, [selectedBidType.value, 'price'], []);
            const regulationPrice = lodashGet(bid, [selectedBidType.value, 'regulationPrice'], []);

            if (capacity.length) {
                const capAndPrice = getInitialValues(tempDate, market, capacity, price, regulationPrice, hidePrice);
                setInitialValues({ ...initialValues, ...capAndPrice, reserveBidIdentification: bid.reserveBidIdentification });
            }
            setLastBidData({ capacity, price, regulationPrice });
        } else {
            const data = {
                capacity: editBid ? lodashGet(existingBid, ['primary', 'capacity'], []) : [],
                price: editBid ? lodashGet(existingBid, ['primary', 'price'], []) : [],
                regulationPrice: editBid ? lodashGet(existingBid, ['primary', 'regulationPrice'], []) : [],
            };
            setLastBidData(data);
        }
    };

    const getOtherMarketsBids = () => {
        service.GetOtherMarketsBid(
            { date: momentTimeFormater(date).format('YYYY-MM-DD'), type: selectedBidType?.value === 'secondary' ? 'both' : selectedBidType?.value, instanceId: instance.value },
            startLoader,
            handleOtherMarketsBidSuccess,
            handleError,
            stopLoader
        );
    };

    const handleOtherMarketsBidSuccess = ({ data }) => {
        setAlreadyBidCreatedHours(lodashGet(data, ['data'], []));
    };

    const getBidRequest = () => {
        service.GetBidRequest(
            {
                date: momentTimeFormater(date).format('YYYY-MM-DD'),
                market,
                instanceId: instance.value,
            },
            startLoader,
            handleBidRequestSuccess,
            handleError,
            stopLoader
        );
    };

    const handleBidRequestSuccess = ({ data }) => {
        let temp = {
            note: null,
            capacity: [],
            price: [],
            ...(isFRR ? { regulationPrice: [] } : {}),
        };
        if (lodashGet(data, ['data'])) {
            temp = {
                ...(lodashGet(data, ['data', 'customBidding'])
                    ? {
                          capacity: lodashGet(data, ['data', 'capacity']),
                          price: lodashGet(data, ['data', 'price']),
                          ...(isFRR ? { regulationPrice: lodashGet(data, ['data', 'regulationPrice'], []) } : {}),
                      }
                    : { capacity: [], price: [], regulationPrice: [] }),
                note: lodashGet(data, ['data', 'note'], null),
            };
        }
        setBidRequest(temp);
    };

    const fillBidRequest = () => {
        if (lodashGet(bidRequest, ['capacity'], []).length) {
            const data = getInitialValues(date, market, bidRequest.capacity, bidRequest.price, bidRequest?.regulationPrice || [], hidePrice);
            setInitialValues({ ...initialValues, ...data });
        } else if (bidRequest.note) toast.warn('Custom bid not available');
        else toast.error('No request found');
    };

    const handleSubmit = (val) => {
        if (!editBid) {
            let payload = {
                ...val,
                instanceId: instance.value,
                customerId,
                date: momentTimeFormater(date).format('YYYY-MM-DD'),
                market: market,
                bidType: selectedBidType.value,
                approvedCapacity: approvedCapacity,
            };
            service.CreateBid(payload, startLoader, handleCreateBidSuccess, handleError, stopLoader);
        } else {
            editFunc({
                ...val,
                instanceId: instance.value,
            });
            closeModal();
        }
    };

    const handleCreateBidSuccess = () => {
        toast.success('Bid Created Successfully');
        closeModal();
        getBids();
    };

    const handleError = (err) => {
        toast.error(lodashGet(err, ['response', 'data', 'message']));
    };

    const getDatahubCostEnergy = () => {
        service.GetDatahubCostEnergy(
            { date: momentTimeFormater(date).format('YYYY-MM-DD'), instanceId: instance.value },
            startLoader,
            handleGetDatahubCostEnergy,
            (err) => {
                handleError(err);
                setDatahubCostEnergy([]);
            },
            stopLoader
        );
    };

    const handleGetDatahubCostEnergy = ({ data }) => {
        setDatahubCostEnergy(lodashGet(data, ['data'], []));
    };

    return (
        <div>
            <div>
                <Typography content={`${market} Bidding`} size="16" />
                <div className={bidClasses.FormWrapper}>
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        innerRef={formikRef}
                        validationSchema={CreateBidValidator(market, approvedCapacity, hidePrice)}
                    >
                        {({ values, errors }) => {
                            return (
                                <Form>
                                    <div className={allDeviceClasses.InputContainer}>
                                        {!editBid && (
                                            <>
                                                <div className={allDeviceClasses.FieldControl}>
                                                    <label>
                                                        Date <span className="required">*</span>
                                                    </label>
                                                    <div className="modal-date-picker">
                                                        <DatePicker date={date} setDate={setDate} disableFutureDate={false} />
                                                    </div>
                                                </div>
                                                {['FCR-D-INC', 'FCR-D-DEC'].includes(market) && (
                                                    <div className={allDeviceClasses.FieldControl}>
                                                        <label>
                                                            Bid Type <span className="required">*</span>
                                                        </label>
                                                        <div>
                                                            <Dropdown name="bidType" options={BID_TYPES} defaultValue={selectedBidType} onChange={setSelectedBidType} />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {['FCR-D-INC', 'FCR-D-DEC', 'FFR'].includes(market) && (
                                            <div className={allDeviceClasses.FieldControl}>
                                                <label>Reserve Bid Identification</label>
                                                <Input name="reserveBidIdentification" id="reserveBidIdentification" style={{ marginTop: '0' }} />
                                            </div>
                                        )}
                                        <div className={allDeviceClasses.FieldControl}>
                                            <label>Instance</label>
                                            <InputWithOutState name="instanceId" id="instanceId" value={instance.label} style={{ marginTop: '0' }} disabled />
                                        </div>
                                        <div className={allDeviceClasses.FieldControl}>
                                            <label>Bid Manage Type</label>
                                            <ToggleButton values={values} label={'External'} name={'isExternal'} />
                                        </div>
                                    </div>
                                    {bidRequest.note && (
                                        <div className={allDeviceClasses.FieldControl2}>
                                            <label>Note</label>
                                            <TextArea
                                                name={`note`}
                                                label="Note"
                                                disabled={true}
                                                value={`${`Bid Request note - ${bidRequest.note}\n`}`}
                                                style={{ fontSize: '0.8vw' }}
                                            />
                                        </div>
                                    )}

                                    {[
                                        {
                                            label: 'Capacity (Mw)',
                                            value: 'capacity',
                                        },
                                        ...(hidePrice
                                            ? []
                                            : [
                                                  {
                                                      label: 'Price (€)',
                                                      value: 'price',
                                                  },
                                              ]),
                                        ...(isFRR
                                            ? [
                                                  {
                                                      label: 'Regulation Price (€)',
                                                      value: 'regulationPrice',
                                                  },
                                              ]
                                            : []),
                                    ].map((type) => (
                                        <div>
                                            <div className={bidClasses.Flex} style={{ justifyContent: 'space-between' }}>
                                                <div className={bidClasses.Subtitle}>{type.label}</div>
                                                {type.value === 'capacity' && (
                                                    <div>
                                                        <button type="button" className="btn-primary" onClick={() => fillBidRequest()}>
                                                            Fill ( Bid Request )
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            <div className={bidClasses.CreateBidForm}>
                                                {lodashGet(values, [type.value]).map((val, index) => (
                                                    <div>
                                                        <div className={allDeviceClasses.FieldControl}>
                                                            <label htmlFor={`${type.value}.${index}`}>
                                                                Slot {index + 1} {' / Hour '}
                                                                {momentTimeFormater(date).startOf('day').clone().add(index, 'hour').hour()}-
                                                                {momentTimeFormater(date)
                                                                    .startOf('day')
                                                                    .clone()
                                                                    .add(index + 1, 'hour')
                                                                    .hour()}
                                                                {type.value === 'price' ? ' Price' : ' '}
                                                                <span className="required">*</span>
                                                                {type.value === 'regulationPrice' && (datahubCostEnergy.length || cost > 0) ? (
                                                                    <span className="required"> [{lodashGet(datahubCostEnergy, [index], 0) + cost}]</span>
                                                                ) : (
                                                                    ''
                                                                )}
                                                            </label>
                                                            <Input
                                                                name={`${type.value}.${index}`}
                                                                id={`${type.value}.${index}`}
                                                                type={'number'}
                                                                disabled={alreadyBidCreatedHours.includes(index) ? true : false}
                                                                style={{ marginTop: '0' }}
                                                                step="0.1" // increasing or decreasing number by 0.1
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </Form>
                            );
                        }}
                    </Formik>
                </div>

                <div className={allDeviceClasses.ButtonContainer}>
                    <div>
                        <button type="button" className="btn-secondary" onClick={() => closeModal()}>
                            Cancel
                        </button>
                    </div>
                    <div>
                        <button type="submit" className="btn-primary" onClick={() => formikRef.current.submitForm()}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateBid;
